import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Activity, AlertCircle } from "lucide-react";
import { agentsApi } from "../lib/ledgerApi";
import { useAuth } from "../contexts/AuthContext";

export default function AgentActivityCard() {
  const { business } = useAuth();
  const [agents, setAgents] = useState([]);
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!business) return;
    let alive = true;
    const tick = async () => {
      try {
        const [a, p, r] = await Promise.all([
          agentsApi.list().catch(() => []),
          agentsApi.approvals().catch(() => []),
          agentsApi.recentPayments(50).catch(() => []),
        ]);
        if (alive) {
          setAgents(a);
          setPending(p);
          setRecent(r);
        }
      } catch {}
    };
    tick();
    const i = setInterval(tick, 7000);
    return () => { alive = false; clearInterval(i); };
  }, [business]);

  const activeCount = agents.filter((a) => a.status === "active").length;
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  const todayUsd = recent
    .filter((it) => it.status === "consumed" && new Date(it.created_at).getTime() >= cutoff)
    .reduce((acc, it) => acc + Number(it.approx_usd || 0), 0);

  if (agents.length === 0 && pending.length === 0) {
    return (
      <div className="border border-border rounded-sm bg-card p-5" data-testid="agent-activity-card">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Agent activity
          </div>
        </div>
        <div className="text-xs text-muted-foreground mb-2">
          No agents yet.
        </div>
        <Link
          to="/app/developer"
          data-testid="agent-activity-cta"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] uppercase tracking-widest border border-border rounded-sm hover:border-foreground hover:bg-muted transition-colors"
        >
          <Bot className="w-3 h-3" strokeWidth={1.5} /> Create agent
        </Link>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-sm bg-card p-5" data-testid="agent-activity-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Agent activity
          </div>
        </div>
        <Link
          to="/app/developer"
          data-testid="agent-activity-all-link"
          className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
        >
          all →
        </Link>
      </div>

      <div className="text-3xl font-mono" data-testid="agent-activity-active-count">
        {activeCount}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
        active agent{activeCount === 1 ? "" : "s"}
      </div>

      <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-xs font-mono">
        <div className="flex justify-between">
          <span className="text-muted-foreground">24h volume</span>
          <span data-testid="agent-activity-volume">${todayUsd.toFixed(2)}</span>
        </div>
        {pending.length > 0 && (
          <Link
            to="/app/agent-approvals"
            data-testid="agent-activity-pending"
            className="flex items-center justify-between hover:underline"
          >
            <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <AlertCircle className="w-3 h-3" strokeWidth={1.5} />
              pending approvals
            </span>
            <span>{pending.length}</span>
          </Link>
        )}
      </div>
    </div>
  );
}
